import { AnimatePresence, motion, useInView, useReducedMotion } from "motion/react";
import { ArrowDown, BookOpenText, Check, Pause, Play, X } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { CASES } from "@/data/content";
import { cn } from "@/lib/utils";

const EASE = [0.16, 1, 0.3, 1] as const;
// How long each step stays on screen: the bug, the lesson, the skill.
const PHASE_MS = [2600, 3000, 3200];
const END_HOLD_MS = 1600;

type Phase = 0 | 1 | 2;

function StepCard({
  n,
  title,
  active,
  reached,
  onPick,
  aside,
  children,
}: {
  n: number;
  title: string;
  active: boolean;
  reached: boolean;
  onPick: () => void;
  aside?: ReactNode;
  children: ReactNode;
}) {
  return (
    <article
      className={cn(
        "card relative flex min-w-0 flex-col p-5 transition-[box-shadow,transform] duration-500 sm:p-6",
        active && "shadow-[0_0_0_2px_var(--color-ink),0_26px_50px_-26px_rgb(16_16_20/0.45)] lg:-translate-y-1.5",
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <button type="button" onClick={onPick} className="flex items-center gap-3 text-left">
          <span
            className={cn(
              "grid size-7 shrink-0 place-items-center rounded-full font-mono text-[12px] transition-colors duration-300",
              active ? "bg-ink text-white" : reached ? "bg-line-2 text-ink" : "bg-line text-ink-3",
            )}
          >
            {n}
          </span>
          <span className="text-[19px] font-semibold tracking-[-0.02em]">{title}</span>
        </button>
        {aside}
      </div>
      <div className="mt-5 flex flex-1 flex-col">{children}</div>
    </article>
  );
}

/** The arrow between two cards. It starts flowing once the step before it is done. */
function Link({ on }: { on: boolean }) {
  return (
    <div aria-hidden className="flex items-center justify-center py-1 lg:py-0">
      <svg viewBox="0 0 44 16" className="hidden w-11 lg:block">
        <path
          d="M2 8h32"
          fill="none"
          stroke={on ? "var(--color-ink)" : "var(--color-line-2)"}
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray="5 3"
          style={on ? { animation: "flow 0.7s linear infinite" } : undefined}
        />
        <path
          d="M31 3l6 5-6 5"
          fill="none"
          stroke={on ? "var(--color-ink)" : "var(--color-line-2)"}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <ArrowDown className={cn("size-5 lg:hidden", on ? "text-ink" : "text-line-2")} />
    </div>
  );
}

function Side({ kind, children }: { kind: "bug" | "fix"; children: ReactNode }) {
  const bug = kind === "bug";
  const Icon = bug ? X : Check;
  return (
    <div className={cn("rounded-2xl p-4", bug ? "bg-bug-soft" : "bg-fix-soft")}>
      <div className={cn("tag flex items-center gap-2", bug ? "text-bug-ink" : "text-fix-ink")}>
        <span className={cn("grid size-[18px] place-items-center rounded-full text-white", bug ? "bg-bug" : "bg-fix")}>
          <Icon aria-hidden className="size-3" strokeWidth={3.5} />
        </span>
        {bug ? "With the bug" : "After the fix"}
      </div>
      <p className="mt-2 text-[16px] leading-snug">{children}</p>
    </div>
  );
}

const swap = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 },
  transition: { duration: 0.45, ease: EASE },
};

export function Evolve() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { margin: "-20% 0px -20% 0px" });
  const reduced = useReducedMotion();
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState<Phase>(0);
  const [playing, setPlaying] = useState(true);
  const running = playing && inView && !reduced;

  // Without motion, show each case finished instead of stepping through it.
  const shownPhase: Phase = reduced ? 2 : phase;

  useEffect(() => {
    if (!running) return;
    const lastStep = index === CASES.length - 1 && phase === 2;
    const id = setTimeout(
      () => {
        if (phase < 2) setPhase((phase + 1) as Phase);
        else {
          setIndex(lastStep ? 0 : index + 1);
          setPhase(0);
        }
      },
      PHASE_MS[phase]! + (lastStep ? END_HOLD_MS : 0),
    );
    return () => clearTimeout(id);
  }, [index, phase, running]);

  const current = CASES[index]!;
  const learned = index + (shownPhase === 2 ? 1 : 0);
  const pickCase = (i: number) => {
    setIndex(i);
    setPhase(0);
  };

  return (
    <section
      id="evolve"
      className="relative flex min-h-[100svh] snap-start flex-col justify-center overflow-hidden border-t border-line bg-white/60 py-9"
    >
      <div ref={ref} className="shell">
        <div className="flex flex-wrap items-end justify-between gap-x-10 gap-y-6">
          <div>
            <span className="tag inline-flex rounded-full border border-line bg-card px-3 py-1.5">
              How the skill evolves
            </span>
            <h2 className="mt-4 text-[clamp(30px,min(3.9vw,6vh),52px)] leading-[1.02] font-semibold tracking-[-0.04em]">
              One real bug in. One new rule out.
            </h2>
          </div>

          <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Pick a bug">
            {CASES.map((c, i) => (
              <button
                key={c.id}
                type="button"
                onClick={() => pickCase(i)}
                aria-pressed={i === index}
                className={cn(
                  "h-9 rounded-full border px-3.5 text-[13.5px] font-medium transition-colors",
                  i === index
                    ? "border-ink bg-ink text-white"
                    : i < index
                      ? "border-line bg-card text-ink"
                      : "border-line bg-card text-ink-3 hover:text-ink",
                )}
              >
                <span className="mr-1.5 font-mono text-[11px] opacity-70">{i + 1}</span>
                {c.topic}
              </button>
            ))}
            {!reduced && (
              <button
                type="button"
                onClick={() => setPlaying(p => !p)}
                aria-label={playing ? "Pause" : "Play"}
                className="grid size-9 place-items-center rounded-full border border-line bg-card text-ink transition-colors hover:border-line-2"
              >
                {playing ? <Pause className="size-4" /> : <Play className="size-4" />}
              </button>
            )}
          </div>
        </div>

        {/* The row is as tall as the longest case, so nothing below it moves when the case changes. */}
        <div className="mt-7 grid grid-cols-1 items-stretch lg:min-h-[440px] lg:grid-cols-[minmax(0,1fr)_44px_minmax(0,1fr)_44px_minmax(0,1.12fr)]">
          {/* 1 · the data */}
          <StepCard
            n={1}
            title="A real bug, and its fix"
            active={shownPhase === 0}
            reached
            onPick={() => setPhase(0)}
          >
            <AnimatePresence mode="wait" initial={false}>
              <motion.div key={current.id} {...swap} className="flex flex-col gap-2.5">
                <div className="mb-1 flex flex-wrap items-center gap-2 text-[13.5px]">
                  <span className="rounded-full border border-line px-2.5 py-0.5 font-semibold">{current.library}</span>
                  <span className="text-ink-3">{current.does}</span>
                </div>
                <Side kind="bug">{current.bug}</Side>
                <motion.div
                  initial={reduced ? false : { opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.75, ease: EASE }}
                >
                  <Side kind="fix">{current.fix}</Side>
                </motion.div>
              </motion.div>
            </AnimatePresence>
            <p className="mt-auto pt-5 text-[13px] leading-relaxed text-ink-3">
              Same code, one small change. Both versions were run to prove it.
            </p>
          </StepCard>

          <Link on={shownPhase >= 1} />

          {/* 2 · the lesson */}
          <StepCard
            n={2}
            title="The lesson"
            active={shownPhase === 1}
            reached={shownPhase >= 1}
            onPick={() => setPhase(1)}
          >
            <AnimatePresence mode="wait" initial={false}>
              {shownPhase === 0 ? (
                <motion.div key="waiting" {...swap} className="flex flex-col gap-2.5" aria-hidden>
                  <div className="rounded-2xl border border-dashed border-line-2 p-4">
                    <div className="skeleton h-2.5 w-20 rounded-full" />
                    <div className="skeleton mt-3 h-2.5 w-full rounded-full" />
                    <div className="skeleton mt-2 h-2.5 w-3/4 rounded-full" />
                  </div>
                  <div className="rounded-2xl border border-dashed border-line-2 p-4">
                    <div className="skeleton h-2.5 w-28 rounded-full" />
                    <div className="skeleton mt-3 h-2.5 w-full rounded-full" />
                    <div className="skeleton mt-2 h-2.5 w-2/3 rounded-full" />
                  </div>
                </motion.div>
              ) : (
                <motion.div key={current.id} {...swap} className="flex flex-col gap-2.5">
                  <div className="rounded-2xl bg-paper p-4 ring-1 ring-line">
                    <div className="tag">What happened here</div>
                    <p className="mt-2 text-[15.5px] leading-snug text-ink-2">{current.story}</p>
                  </div>
                  <motion.div
                    initial={reduced ? false : { opacity: 0, y: 10, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.55, delay: 0.85, ease: EASE }}
                    className="rounded-2xl bg-skill-soft p-4 ring-1 ring-skill/25"
                  >
                    <div className="tag text-skill-ink">The rule behind it</div>
                    <p className="mt-2 text-[16.5px] leading-snug font-medium">{current.rule}</p>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
            <p className="mt-auto pt-5 text-[13px] leading-relaxed text-ink-3">
              The story stays as a note. Only the rule moves on.
            </p>
          </StepCard>

          <Link on={shownPhase >= 2} />

          {/* 3 · the skill */}
          <StepCard
            n={3}
            title="The skill"
            active={shownPhase === 2}
            reached={shownPhase >= 2}
            onPick={() => setPhase(2)}
            aside={
              <span
                key={learned}
                className="pop flex items-center gap-1.5 rounded-full bg-skill px-2.5 py-1 font-mono text-[11.5px] text-white"
              >
                <BookOpenText aria-hidden className="size-3.5" />v{learned}
              </span>
            }
          >
            <ol className="flex flex-col gap-1.5 text-[13.5px] leading-snug">
              <li className="rounded-xl bg-paper px-3 py-2 text-ink-3 ring-1 ring-line">The starting checklist</li>
              {CASES.map((c, i) => {
                const has = i < learned;
                const fresh = has && i === index && shownPhase === 2;
                return (
                  // Every rule keeps its place from the start, so the card never changes size.
                  <li key={c.id} className="relative">
                    <span
                      aria-hidden={!has}
                      className={cn(
                        "block rounded-xl px-3 py-2 transition-[background-color,box-shadow] duration-700",
                        !has && "invisible",
                        fresh ? "bg-skill-soft ring-1 ring-skill/30" : "bg-card ring-1 ring-line",
                      )}
                    >
                      <span className={cn("mr-1.5 font-mono font-semibold", fresh ? "text-skill-ink" : "text-ink-3")}>
                        +
                      </span>
                      {c.rule}
                    </span>
                    {!has && (
                      <span aria-hidden className="absolute inset-0 rounded-xl border border-dashed border-line-2" />
                    )}
                    {fresh && (
                      <motion.span
                        aria-hidden
                        initial={{ opacity: 0.9, scale: 1 }}
                        animate={{ opacity: 0, scale: 1.06 }}
                        transition={{ duration: 0.9, ease: "easeOut" }}
                        className="pointer-events-none absolute inset-0 rounded-xl ring-2 ring-skill"
                      />
                    )}
                  </li>
                );
              })}
            </ol>
            <p className="mt-auto pt-4 text-[13px] leading-relaxed text-ink-3">
              Any rule can be undone if it makes the AI worse.
            </p>
          </StepCard>
        </div>
      </div>
    </section>
  );
}
